import { initReactI18next } from 'react-i18next';
import i18n from 'i18next';
import Backend from 'i18next-http-backend';

import { ENV } from 'services/environment/environment';
import { UserKeys } from 'support/types/localStorage';

const defaultNS = 'common';

export type AllowedLanguages = 'en' | 'de';
const DEFAULT_LANGUAGE: AllowedLanguages = 'en';
const AllowedLanguagesArray: Array<AllowedLanguages> = ['en', 'de'];

// Add a check to ensure localStorage is available
const getLocalStorageItem = (key: string): string | null => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return localStorage.getItem(key);
    }
    return null;
  } catch (e) {
    console.warn('localStorage is not available:', e);
    return null;
  }
};

const localStorageLanguage = getLocalStorageItem(UserKeys.i18n);

export let currentLanguage: AllowedLanguages =
  localStorageLanguage && AllowedLanguagesArray.includes(localStorageLanguage as AllowedLanguages)
    ? (localStorageLanguage as AllowedLanguages)
    : DEFAULT_LANGUAGE;

export const setLanguage = async (language: AllowedLanguages): Promise<void> => {
  currentLanguage = language;
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(UserKeys.i18n, language);
    }
  } catch (_e) {
    console.warn('Could not save language preference to localStorage:');
  }
  await i18n.changeLanguage(language);
};

i18n
  .use(Backend)
  .use(initReactI18next)
  .init({
    ns: [
      defaultNS,
      'auth',
      'connections',
      'dashboard',
      'inbox',
      'integrations',
      'orders',
      'partners',
      'priceList',
      'priceCatalogs',
      'settings',
      'transactions',
      'webedi',
      'paymentReconciliation',
      'invoiceVerification',
      'orderForms',
    ],
    defaultNS,
    fallbackLng: 'en',
    lng: currentLanguage,
    // Both catalogues are kept in memory so text can be rendered in a language the Portal itself is
    // not showing — the dispute email in Invoice Verification is written in a language of its own.
    // Without this, i18next would only fetch the active language and its fallback, and asking for
    // the other one would silently fall back to English.
    preload: AllowedLanguagesArray,
    interpolation: {
      escapeValue: false,
    },
    returnEmptyString: false,
    returnNull: false,
    returnDetails: false,
    backend: {
      queryStringParams: { v: ENV.SENTRY_RELEASE },
    },
  });
